/**
 * Identificator Tool Page
 *
 * Write unique serial numbers to IoT devices.
 * Tool color: cyan
 *
 * Workflow:
 * 1. Select technology
 * 2. Manage serial ports and detect devices
 * 3. Write serial number and view results
 */

import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  BadgeCheck,
  CheckCircle,
  Clock,
  Loader2,
  Plus,
  Settings,
  XCircle,
  Zap,
} from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router';
import { type ITechnology, technologyApi } from '@/api/production/technologies';
import { ConsoleDisplay } from '@/components/production/ConsoleDisplay';
import { TechnologySearchForm } from '@/components/production/TechnologySearchForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useIdentification } from '@/hooks/production/useIdentification';
import { cn } from '@/lib/utils';
import type { IIdentificationLog } from '@/types/production/identification-log';

// ============================================
// Types
// ============================================

// ============================================
// Status Badge Component
// ============================================

function DeviceStatusBadge({
  status,
}: {
  status: 'fully_identified' | 'partially_identified' | 'not_identified';
}) {
  const config = {
    fully_identified: {
      label: 'Identificado',
      className: 'bg-green-100 text-green-800',
      dotClass: 'bg-green-500',
    },
    partially_identified: {
      label: 'Parcialmente Identificado',
      className: 'bg-yellow-100 text-yellow-800',
      dotClass: 'bg-yellow-500',
    },
    not_identified: {
      label: 'Não Identificado',
      className: 'bg-red-100 text-red-800',
      dotClass: 'bg-red-500',
    },
  };

  const { label, className, dotClass } = config[status];

  return (
    <div className="flex items-center gap-2">
      <div className={cn('h-2 w-2 rounded-full', dotClass)} />
      <span className={cn('text-xs font-medium', className)}>{label}</span>
    </div>
  );
}

// ============================================
// Detected Devices Table
// ============================================

function DetectedDevicesTable({
  devices,
}: {
  devices: Array<{ port: any; equipment?: any; status: any }>;
}) {
  if (devices.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Nenhum dispositivo detectado</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Conecte uma porta serial e aguarde a detecção automática
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Serial</TableHead>
            <TableHead>IMEI</TableHead>
            <TableHead>ICCID</TableHead>
            <TableHead>Firmware</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device, index) => (
            <TableRow key={device.equipment?.serial || index}>
              <TableCell>
                <DeviceStatusBadge status={device.status} />
              </TableCell>
              <TableCell className="font-mono text-sm">
                {device.equipment?.serial ?? '--'}
              </TableCell>
              <TableCell className="font-mono text-sm">{device.equipment?.imei ?? '--'}</TableCell>
              <TableCell className="font-mono text-sm">{device.equipment?.iccid ?? '--'}</TableCell>
              <TableCell className="font-mono text-sm">
                {device.equipment?.firmware ?? '--'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ============================================
// Identified Devices Table
// ============================================

function IdentifiedDevicesTable({ devices }: { devices: IIdentificationLog[] }) {
  if (devices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground mb-2">
          Nenhum equipamento identificado
        </h3>
        <p className="text-sm text-muted-foreground max-w-md">
          Complete as etapas anteriores para visualizar os equipamentos identificados.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Resultado</TableHead>
            <TableHead>Serial Anterior</TableHead>
            <TableHead>Serial Novo</TableHead>
            <TableHead>Data</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device, index) => (
            <TableRow key={device.id ?? `identified-${index}`}>
              <TableCell>
                {device.status ? (
                  <Badge variant="success-subtle" className="gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Identificado
                  </Badge>
                ) : (
                  <Badge variant="destructive-subtle" className="gap-1">
                    <XCircle className="h-3 w-3" />
                    Falhou
                  </Badge>
                )}
              </TableCell>
              <TableCell className="font-mono text-sm text-muted-foreground">
                {device.equipment_before?.serial ?? '(vazio)'}
              </TableCell>
              <TableCell className="font-mono text-sm text-black font-semibold">
                {device.equipment_after?.serial ?? '(vazio)'}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {new Date(device.created_at).toLocaleString('pt-BR')}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ============================================
// Main Page Component
// ============================================

export default function IdentificatorPage() {
  const [searchParams] = useSearchParams();
  const technologyId = searchParams.get('technology_id');

  // State
  const [selectedTechnology, setSelectedTechnology] = React.useState<ITechnology | null>(null);
  const [consoleLogs, setConsoleLogs] = React.useState<string[]>([]);
  const [newSerialNumber, setNewSerialNumber] = React.useState('');
  const [rawInputValue, setRawInputValue] = React.useState('');
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch technology if ID is in URL
  const { data: technologyData } = useQuery({
    queryKey: ['technology', technologyId],
    queryFn: () => technologyApi.findOne(technologyId!),
    enabled: !!technologyId,
  });

  // Set technology from URL param
  React.useEffect(() => {
    if (technologyData) {
      setSelectedTechnology(technologyData);
    }
  }, [technologyData]);

  const date = new Date();

  // Add log helper
  const addLog = React.useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString('pt-BR');
    setConsoleLogs((prev) => [...prev, `[${timestamp}] ${message}`]);
  }, []);

  // Clear logs
  const clearLogs = React.useCallback(() => {
    setConsoleLogs([]);
  }, []);

  // Helper to format response values for logging
  const formatResponseValue = React.useCallback((value: any): string => {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value.join(',');
    }
    if (typeof value === 'object') {
      // Try to extract meaningful values from the object
      const values = Object.values(value).filter((v) => v !== null && v !== undefined);
      if (values.length === 1 && typeof values[0] === 'string') {
        return values[0];
      }
      if (values.length === 1) {
        return String(values[0]);
      }
      return JSON.stringify(value);
    }
    return String(value);
  }, []);

  // Handle technology change
  const handleTechnologyChange = React.useCallback(
    (tech: ITechnology | null) => {
      setSelectedTechnology(tech);
      setNewSerialNumber('');
      clearLogs();
      if (tech) {
        addLog(`Tecnologia selecionada: ${tech.name.brand}`);
      }
    },
    [addLog, clearLogs],
  );

  // Use the identification hook
  const { detected, identified, identify, requestPort, isIdentifying } = useIdentification({
    technology: selectedTechnology,
  });

  // Log when devices are detected
  const detectedSerialsRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    const currentSerials = new Set(
      detected.filter((d) => d.equipment?.serial).map((d) => d.equipment?.serial!),
    );
    const newSerials = Array.from(currentSerials).filter(
      (serial) => !detectedSerialsRef.current.has(serial),
    );

    newSerials.forEach((serial) => {
      addLog(`Dispositivo detectado: ${serial}`);
    });

    detectedSerialsRef.current = currentSerials;

    if (detected.length === 0 && detectedSerialsRef.current.size > 0) {
      addLog('Nenhum dispositivo detectado');
      detectedSerialsRef.current.clear();
    }
  }, [detected, addLog]);

  // Log when identification completes
  const lastIdentifiedLengthRef = React.useRef(0);
  React.useEffect(() => {
    const newCount = identified.length - lastIdentifiedLengthRef.current;
    if (newCount > 0) {
      const newDevices = identified.slice(-newCount);
      newDevices.forEach((device) => {
        if (device.status) {
          addLog(`✓ Identificação bem-sucedida`);
          addLog(`  Serial anterior: ${device.equipment_before?.serial || '(vazio)'}`);
          addLog(`  Serial gravado: ${device.equipment_after?.serial || '(vazio)'}`);
          // Log messages from identification
          device.messages?.forEach((msg) => {
            addLog(`  → ${msg.key}: ${msg.request}`);
            if (msg.response) {
              const responseStr = formatResponseValue(msg.response);
              if (responseStr) {
                addLog(`  ← ${responseStr}`);
              }
            }
          });
        } else {
          addLog(`✗ Falha na identificação`);
        }
      });
    }
    lastIdentifiedLengthRef.current = identified.length;
  }, [identified, addLog, formatResponseValue]);

  // Write serial number with logging
  const handleIdentify = React.useCallback(async () => {
    if (!newSerialNumber || !selectedTechnology) return;

    addLog(`Iniciando gravação do serial: ${newSerialNumber}`);

    // Check if this is BWS 4G technology (requires restart). Precisa conter
    // "bws" E "4g" — outras BWS (NB2, NB2 LoRa, LoRa) não reiniciam.
    const isBWS4G =
      (selectedTechnology.name.system?.toLowerCase().includes('bws') &&
        selectedTechnology.name.system?.toLowerCase().includes('4g')) ||
      (selectedTechnology.name.brand?.toLowerCase().includes('bws') &&
        selectedTechnology.name.brand?.toLowerCase().includes('4g'));

    if (isBWS4G) {
      addLog('BWS 4G detectado - iniciando processo com reinício (~40s)...');
      addLog('Enviando comando de gravação...');
      addLog('Reiniciando dispositivo...');
      addLog('Aguardando reinício (40s)...');
    }

    await identify(newSerialNumber, detected, selectedTechnology);
    setNewSerialNumber('');
    setRawInputValue('');
  }, [identify, detected, selectedTechnology, newSerialNumber, addLog]);

  return (
    <div className="container mx-auto pb-8 px-4 space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-base font-semibold leading-7 text-gray-900">Gravador de Serial</h1>
        <p className="mt-2 text-sm text-gray-700">
          Ferramenta para identificar <span className="text-gray-900">equipamentos IoT.</span> Data
          de hoje: <time dateTime={date.toLocaleDateString()}>{date.toLocaleDateString()}</time>
        </p>
      </div>

      {/* Step 1: Technology Selection */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              variant="default"
              className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white"
            >
              1
            </Badge>
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-black" />
              <CardTitle className="text-xl">Definição da tecnologia</CardTitle>
            </div>
          </div>
          <CardDescription className="ml-11">Escolha a tecnologia dos equipamentos</CardDescription>
        </CardHeader>
        <CardContent className="ml-11">
          <TechnologySearchForm
            technology={selectedTechnology}
            showConfigurationProfile={false}
            showSerialNumber={false}
            onTechnologyChange={handleTechnologyChange}
          />
        </CardContent>
      </Card>

      {/* Step 2: Port Management */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              variant={selectedTechnology ? 'default' : 'secondary'}
              className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white"
            >
              2
            </Badge>
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-black" />
              <CardTitle className="text-xl">Gerenciamento de Portas</CardTitle>
            </div>
          </div>
          <CardDescription className="ml-11">
            Visualize e identifique os equipamentos conectados às portas seriais
          </CardDescription>
        </CardHeader>
        {selectedTechnology ? (
          <CardContent className="ml-11 space-y-6">
            <DetectedDevicesTable devices={detected} />

            {/* BWS 4G Progress Indicator */}
            {isIdentifying &&
              selectedTechnology?.name.system?.toLowerCase().includes('bws') &&
              selectedTechnology?.name.system?.toLowerCase().includes('4g') && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-blue-900">
                        Processo BWS 4G em andamento
                      </h4>
                      <p className="text-xs text-blue-700">
                        O dispositivo precisa reiniciar para gravar o serial. Após reinício, ele
                        será detectado automaticamente.
                      </p>
                    </div>
                  </div>

                  {/* Progress Steps */}
                  <div className="space-y-2 text-xs">
                    {[
                      'Enviando comando de gravação...',
                      'Reiniciando dispositivo...',
                      'Aguardando reinício (40s)...',
                      'Dispositivo será detectado automaticamente',
                    ].map((step) => (
                      <div key={step} className="flex items-center gap-2 text-gray-600">
                        <div className="h-3.5 w-3.5 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>

                  {/* Estimated Time */}
                  <div className="flex items-center gap-2 text-xs text-blue-600 pt-2 border-t border-blue-200">
                    <Clock className="h-3.5 w-3.5" />
                    <span>Tempo estimado: ~40 segundos</span>
                  </div>
                </div>
              )}

            <Separator />

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex flex-col sm:flex-row gap-3 items-end">
                <div className="space-y-2">
                  <Label htmlFor="serial-number">Novo Número de Série</Label>
                  <Input
                    id="serial-number"
                    placeholder="Digite o serial"
                    value={rawInputValue}
                    onChange={(e) => {
                      // Remove qualquer whitespace (tab, espaço, quebra de linha) — comum em colagem de planilha.
                      const value = e.target.value.replace(/\s+/g, '').toUpperCase();

                      // Atualiza o input imediatamente para feedback visual
                      setRawInputValue(value);

                      // Cancela o timer anterior se existir
                      if (debounceTimerRef.current) {
                        clearTimeout(debounceTimerRef.current);
                      }

                      // Aguarda 300ms após a última digitação antes de processar
                      debounceTimerRef.current = setTimeout(() => {
                        // Extrai SN ou IMEI baseado no tipo de dispositivo:
                        // Formato antigo (separador ':'):
                        // BWS 4G: BWS 4G SN:0301142E IMEI:357746432051594 ANATEL:03134-25-13041 (extrai IMEI)
                        // BWS NB2: BWS NB2 SN:0001B9DF ANATEL:00525-25-13041 (extrai SN)
                        // Formato novo (separador 'Ç'):
                        // BWS 4G: BWS 4G SNÇ0301B50F IMEIÇ357746432463443 ANATELÇ03134-25-13041 (extrai IMEI)
                        // BWS IOT: BWS IOT SNÇ0001000D ANATELÇ00525-25-13041 (extrai SN)

                        // Primeiro identifica o tipo de dispositivo pelo nome.
                        // BWS LL NB e BWS BAIT NB seguem o NB2: o serial vem da etiqueta e o IMEI
                        // do cadastro (serial-relations). `\s*` porque os espaços já foram
                        // removidos acima.
                        const isNB2 =
                          /BWS\s+(NB2|IOT)/i.test(value) || /BWS\s*(LL|BAIT)\s*NB/i.test(value);
                        const isBWS4G = /BWS\s+4G/i.test(value);

                        let extractedValue: string;
                        let displayValue: string;

                        if (isNB2) {
                          // Para NB2/IOT/LL NB/BAIT NB, extrai o SN (suporta ':' ou 'Ç')
                          const snMatch = value.match(/SN[:Ç]([A-F0-9]{8})/i);
                          if (snMatch) {
                            extractedValue = snMatch[1];
                            displayValue = extractedValue;
                          } else {
                            extractedValue = value;
                            displayValue = value.replace(/\s*ANATEL[:Ç].*$/, '').trim();
                          }
                        } else if (isBWS4G) {
                          // Para BWS 4G, extrai o IMEI (suporta ':' ou 'Ç')
                          const imeiMatch = value.match(/IMEI[:Ç](\d{15})/i);
                          if (imeiMatch) {
                            extractedValue = imeiMatch[1];
                            displayValue = extractedValue;
                          } else {
                            extractedValue = value;
                            displayValue = value.replace(/\s*ANATEL[:Ç].*$/, '').trim();
                          }
                        } else {
                          // Fallback para formato genérico (IMEI tem prioridade)
                          const imeiMatch = value.match(/IMEI[:Ç](\d{15})/i);
                          const snMatch = value.match(/SN[:Ç]([A-F0-9]{8})/i);
                          if (imeiMatch) {
                            extractedValue = imeiMatch[1];
                            displayValue = extractedValue;
                          } else if (snMatch) {
                            extractedValue = snMatch[1];
                            displayValue = extractedValue;
                          } else {
                            extractedValue = value;
                            displayValue = value.replace(/\s*ANATEL[:Ç].*$/, '').trim();
                          }
                        }

                        setNewSerialNumber(extractedValue);
                        setRawInputValue(displayValue);
                      }, 1200);
                    }}
                    className="w-48 font-mono"
                  />
                </div>
                <Button
                  onClick={handleIdentify}
                  disabled={!newSerialNumber || detected.length === 0 || isIdentifying}
                  className="min-w-35 bg-black hover:bg-gray-800 text-white"
                >
                  {isIdentifying ? (
                    <>
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      Gravando
                    </>
                  ) : (
                    <>
                      <BadgeCheck className="mr-2 h-4 w-4" />
                      Gravar Serial
                    </>
                  )}
                </Button>
              </div>

              <Button variant="outline" onClick={requestPort} className="min-w-35">
                <Plus className="mr-2 h-4 w-4" />
                Nova Porta
              </Button>
            </div>
          </CardContent>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">
              Nenhuma tecnologia selecionada
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Complete as etapas anteriores para visualizar os equipamentos detectados.
            </p>
          </div>
        )}
      </Card>

      {/* Step 3: Results */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              variant={identified.length > 0 ? 'default' : 'secondary'}
              className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white"
            >
              3
            </Badge>
            <div className="flex items-center gap-2">
              <CheckCircle
                className={`h-5 w-5 ${identified.length > 0 ? 'text-black' : 'text-muted-foreground'}`}
              />
              <CardTitle className="text-xl">Verificação e Resultados</CardTitle>
            </div>
          </div>
          <CardDescription className="ml-11">
            {identified.length > 0
              ? `${identified.length} equipamento(s) identificado(s) com sucesso`
              : 'Aguardando identificação dos equipamentos'}
          </CardDescription>
        </CardHeader>
        {selectedTechnology ? (
          <CardContent className="ml-11 space-y-6">
            <IdentifiedDevicesTable devices={identified} />

            <ConsoleDisplay
              logs={consoleLogs}
              height={200}
              title="Log de Execução"
              textColor="green"
              bgColor="gray"
              showLineNumbers={false}
              onClear={clearLogs}
            />
          </CardContent>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">
              Nenhuma tecnologia selecionada
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Complete as etapas anteriores para visualizar os equipamentos detectados.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}

export { IdentificatorPage };
